// ============================================================================
// Mock DÉDIÉ aux abonnements (2026-09-28) — activé par
// VITE_MOCK_SUBSCRIPTION=trial|debutant|classique|pro, indépendant de
// VITE_USE_MOCKS (handlers v1 désalignés) et de VITE_MOCK_TREND.
// SEULES les lectures d'abonnement/quotas sont interceptées (abonnement
// courant, quotas d'analyses, quotas de veille, éligibilité essai, plans
// disponibles) : de quoi voir l'app dans chaque état de plan — paywall
// Tendances en essai, alertes de quotas, CTA d'essai, plan courant sur la
// page pricing. Les MUTATIONS (checkout Stripe…) partent au vrai serveur et
// échoueront sur les ids mockés : mock d'AFFICHAGE uniquement.
// ============================================================================

const PLAN_VALUES = ['trial', 'debutant', 'classique', 'pro'] as const;
export type MockSubscriptionPlan = (typeof PLAN_VALUES)[number];

const rawFlag = import.meta.env.VITE_MOCK_SUBSCRIPTION as string | undefined;

export const SUBSCRIPTION_MOCK_PLAN: MockSubscriptionPlan | null = PLAN_VALUES.includes(rawFlag as MockSubscriptionPlan)
  ? (rawFlag as MockSubscriptionPlan)
  : null;

export const SUBSCRIPTION_MOCK_ENABLED = SUBSCRIPTION_MOCK_PLAN != null;

interface MockPlanSpec {
  id: string;
  name: string;
  priceMonthly: number;
  catalog: number;
  profile: number;
  veille: number;
  /** Accès à la recherche Vestiaire Collective (2026-10-01 : tous les plans sauf Débutant). */
  vestiaireAccess: boolean;
}

// Grille de plans de la page pricing (specs 2026-08) — l'essai porte les
// quotas réduits 8/5/3 sur le plan Débutant
const PLANS: Record<Exclude<MockSubscriptionPlan, 'trial'>, MockPlanSpec> = {
  debutant: {
    id: 'mock-plan-debutant',
    name: 'Débutant',
    priceMonthly: 11.99,
    catalog: 25,
    profile: 10,
    veille: 3,
    vestiaireAccess: false,
  },
  classique: {
    id: 'mock-plan-classique',
    name: 'Classique',
    priceMonthly: 39.99,
    catalog: 90,
    profile: 50,
    veille: 15,
    vestiaireAccess: true,
  },
  pro: {
    id: 'mock-plan-pro',
    name: 'Pro',
    priceMonthly: 79.99,
    catalog: 250,
    profile: 200,
    veille: 50,
    vestiaireAccess: true,
  },
};

const TRIAL_QUOTAS = { catalog: 8, profile: 5, veille: 3 };

/** Retourne la réponse mockée pour les lectures d'abonnement, undefined sinon. */
export const resolveSubscriptionMock = (query: string): unknown => {
  if (SUBSCRIPTION_MOCK_PLAN == null) return undefined;
  const trialing = SUBSCRIPTION_MOCK_PLAN === 'trial';
  // L'essai porte sur le plan Débutant (quotas réduits 8/5/3)
  const planKey: Exclude<MockSubscriptionPlan, 'trial'> =
    SUBSCRIPTION_MOCK_PLAN === 'trial' ? 'debutant' : SUBSCRIPTION_MOCK_PLAN;
  const plan = PLANS[planKey];
  const quotas = trialing ? TRIAL_QUOTAS : { catalog: plan.catalog, profile: plan.profile, veille: plan.veille };

  if (/^\s*query\s+GetCurrentUserSubscription\b/.test(query)) {
    return {
      getCurrentUserSubscription: {
        isActive: true,
        isTrialing: trialing,
        inactivatedAt: null,
        plan: {
          id: plan.id,
          name: plan.name,
          periodicity: 'monthly',
          price: plan.priceMonthly,
          // Essai : nextRenewalAt = fin d'essai (J+5) ; sinon prochain cycle
          nextRenewalAt: new Date(Date.now() + (trialing ? 5 : 23) * 86_400_000).toISOString(),
        },
      },
    };
  }

  if (/^\s*query\s+GetCurrentUserAnalysisQuotas\b/.test(query)) {
    const catalogUsed = Math.min(3, quotas.catalog);
    const profileUsed = Math.min(1, quotas.profile);
    return {
      getCurrentUserAnalysisQuotas: {
        hasPartialPublicAnalysisAccess: true,
        hasFullPublicAnalysisAccess: !trialing,
        catalogAnalysesUsed: catalogUsed,
        catalogAnalysesMax: quotas.catalog,
        catalogAnalysesAvailable: quotas.catalog - catalogUsed,
        profileAnalysesUsed: profileUsed,
        profileAnalysesMax: quotas.profile,
        profileAnalysesAvailable: quotas.profile - profileUsed,
        canCreateCatalogAnalysis: true,
        canCreateProfileAnalysis: true,
      },
    };
  }

  if (/^\s*query\s+GetCurrentUserVeilleQuotas\b/.test(query)) {
    return {
      getCurrentUserVeilleQuotas: {
        veillesUsed: 1,
        veillesMax: quotas.veille,
        veillesAvailable: quotas.veille - 1,
        canCreateVeille: true,
      },
    };
  }

  // L'essai porte sur le plan Débutant : pas d'accès Vestiaire
  if (/^\s*query\s+GetCurrentUserVestiaireAccess\b/.test(query)) {
    return { getCurrentUserVestiaireAccess: plan.vestiaireAccess };
  }

  if (/^\s*query\s+GetCanStartFreeTrial\b/.test(query)) {
    // Déjà en essai ou déjà abonné : plus éligible
    return { getCanStartFreeTrial: false };
  }

  if (/^\s*query\s+GetAvailableSubscriptionPlans\b/.test(query)) {
    return {
      getAvailableSubscriptionPlans: (['debutant', 'classique', 'pro'] as const).map((key) => ({
        id: PLANS[key].id,
        name: PLANS[key].name,
        isPublic: true,
        priceMonthly: PLANS[key].priceMonthly,
        // Annuel : deux mois offerts
        priceYearly: Math.round(PLANS[key].priceMonthly * 10 * 100) / 100,
        catalogAnalysisQuotas: PLANS[key].catalog,
        profileAnalysisQuotas: PLANS[key].profile,
        trialPeriodDays: 7,
        trialCatalogAnalysisQuotas: TRIAL_QUOTAS.catalog,
        trialProfileAnalysisQuotas: TRIAL_QUOTAS.profile,
        trialVeilleQuotas: TRIAL_QUOTAS.veille,
      })),
    };
  }

  return undefined;
};
